import { indexBy, set } from '@cicero/data/utils'
import { Attr, IModelLifecycle, IModelSchemaConfig, Without } from './schema'
import { GET_UNSYNCED_ENTITIES, MERGE_IN_DOC_CHANGES, PREPARE_DOC_FOR_SYNC } from './syncing'
import { MERGE_ALLOWED_PARAMS_INTO_DOC, PREPARE_DOC_FOR_SAVE } from './crud'
import { SYNC_MODELS_OVER_HTTP } from 'actions/SYNC_MODELS_OVER_HTTP'
import { QUERY_MODELS_OVER_HTTP } from 'actions/QUERY_MODELS_OVER_HTTP'
import { FETCH_MODEL_OVER_HTTP } from 'actions/FETCH_MODEL_OVER_HTTP'

/**
 * The actual schema for a model instance 
 */
export class BaseSchema {
  static NAME: string
  static CONFIG: IModelSchemaConfig
  static QUERIES = {
    queryIds(ids: string[]) { return { selector: { $or: [{ id: { $in: ids } }, { 'meta.uuid': { $in: ids } }] }  } },
  }
  static UTILS = {
    indexBy,
    pluckId(doc: BaseSchema) { return doc?.meta?.uuid || doc?.id },
  }
  static LIFECYCLE: IModelLifecycle = {
    async loadSyncQueue(coll) {
      return GET_UNSYNCED_ENTITIES(coll)
    },

    async onChange(store, change) {
      store.events.push(change)
      store.schedule(SYNC_MODELS_OVER_HTTP)
    },

    cacheKey(schema, params) {
      const { selector, modifier } = params
      return `${schema.CONFIG.name_plural}:${new URLSearchParams({ ...selector, ...modifier }).toString()}`
    },

    cachePeriod(schema, params) {
      return params?.selector?.id ? 5000 : 30000
    },

    merge(current, changes) {
      return MERGE_IN_DOC_CHANGES(current, changes)
    },

    preInit(schema) {
      schema.CONFIG.local.push('meta')
      schema.CONFIG.initialize()
      return schema
    },

    preChange(doc) {
      return PREPARE_DOC_FOR_SAVE(doc)
    },

    preSync(doc, schema) {
      return PREPARE_DOC_FOR_SYNC(doc, schema)
    },

    sanitize(schema, params) {
      return MERGE_ALLOWED_PARAMS_INTO_DOC(schema.CONFIG, params)
    },

    fetchQueryDispatch(store, model, params) {
      store.dispatch({
        type: QUERY_MODELS_OVER_HTTP,
        payload: { model: model.schema.CONFIG.name_plural, params },
        beforeHook() { model.loading$.startRequest() },
        afterHook() { model.loading$.completeRequest() },
      })
    },

    fetchDispatch(store, model, params) {
      store.dispatch({
        type: FETCH_MODEL_OVER_HTTP,
        payload: { model: model.schema.CONFIG.name_plural, params },
        beforeHook() { model.loading$.startRequest() },
        afterHook() { model.loading$.completeRequest() },
      })
    },

    transformQuery(query) {
      if (query.with_deleted !== true) { set(query, 'selector/meta.deleted_offline_at/$exists', false, '/') }
      return query
    },
  }

  @Attr({ type: 'string', primary: true, required: true })
  id: string

  @Attr({ type: 'string', format: 'date-time' })
  created_at?: string

  @Attr({ type: 'string', format: 'date-time' })
  updated_at?: string

  @Attr({ type: ['string', 'null'], format: 'date-time' })
  deleted_at?: string

  @Attr({
    type: 'object',
    properties: {
      uuid: { type: 'string' },
      created_offline_at: { type: 'number' },
      deleted_offline_at: { type: 'number' },
      fresh: { type: 'boolean' },
      last_synced_at: { type: 'number' },
      raw: { type: 'object' },
      updated_offline_at: { type: 'number' },
    },
    required: true,
  }) meta: {
    uuid?: string
    created_offline_at?: number
    deleted_offline_at?: number
    fresh?: boolean
    last_synced_at?: number
    raw?: any
    updated_offline_at?: number
  } = {}
}

export type BaseSchemaCreateParams<T extends BaseSchema> = Without<T, 'id'|'meta'>
